import React, { useState } from 'react';
import { ExamPaperQuestionItem } from '../../../api/exam';
import { cn } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { LatexText } from '../../../components/ui/LatexText';
import { Button } from '../../../components/ui/button';
import { Plus, Trash2, ChevronDown, ChevronRight, FileText } from 'lucide-react';

const GROUP_TYPE_LABEL: Record<string, string> = {
  reading_comp: '阅读理解',
  cloze: '完形填空',
  listening: '听力',
  seven_choose_five: '七选五',
  grammar_fill: '语法填空',
  matching: '信息匹配',
}

interface QuestionListProps {
  questions: ExamPaperQuestionItem[];
  selectedQuestionId: string | null;
  onSelect: (questionId: string) => void;
  onSelectGroup: (groupId: string) => void;
  onInsertAfter: (afterQuestionId: string | null) => void;
  onDelete: (questionId: string) => void;
}

/** A group entry: either a single question or a composite group */
type ListItem =
  | { kind: 'single'; question: ExamPaperQuestionItem; flatIndex: number }
  | { kind: 'group'; groupId: string; groupType: string; passagePreview: string; subQuestions: ExamPaperQuestionItem[]; flatIndices: number[] }

function groupQuestions(questions: ExamPaperQuestionItem[]): ListItem[] {
  const items: ListItem[] = []
  const seen = new Set<string>()

  questions.forEach((q, flatIndex) => {
    const gid = q.group_id
    if (gid) {
      if (seen.has(gid)) return // already added this group
      seen.add(gid)
      const subs = questions.filter(x => x.group_id === gid)
      const flatIndices = questions.reduce<number[]>((acc, x, i) => {
        if (x.group_id === gid) acc.push(i)
        return acc
      }, [])
      items.push({
        kind: 'group',
        groupId: gid,
        groupType: q.group_type || 'reading_comp',
        passagePreview: (q.passage_content || '').split('\n')[0]?.slice(0, 60) || '',
        subQuestions: subs,
        flatIndices,
      })
    } else {
      items.push({ kind: 'single', question: q, flatIndex })
    }
  })

  return items
}

export const QuestionList: React.FC<QuestionListProps> = ({
  questions,
  selectedQuestionId,
  onSelect,
  onSelectGroup,
  onInsertAfter,
  onDelete,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const items = groupQuestions(questions)

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }

  // Check if any sub-question in a group is selected
  const isGroupSelected = (group: ListItem & { kind: 'group' }) =>
    group.subQuestions.some(sq => sq.question_id === selectedQuestionId)

  return (
    <div className="flex flex-col h-full border-r bg-background">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">题目列表 ({questions.length})</h2>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onInsertAfter(questions.length ? questions[questions.length - 1].question_id : null)}
            className="gap-1"
          >
            <Plus className="h-4 w-4" />
            添加题目
          </Button>
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-2">
          {items.map((item, displayIndex) => {
            if (item.kind === 'single') {
              const q = item.question
              const isSelected = q.question_id === selectedQuestionId
              return (
                <QuestionRow
                  key={q.question_id}
                  q={q}
                  index={displayIndex + 1}
                  isSelected={isSelected}
                  onSelect={() => onSelect(q.question_id)}
                  onInsertAfter={() => onInsertAfter(q.question_id)}
                  onDelete={() => onDelete(q.question_id)}
                />
              )
            }

            // Group item
            const group = item
            const expanded = expandedGroups.has(group.groupId)
            const selected = isGroupSelected(group)
            const groupLabel = GROUP_TYPE_LABEL[group.groupType] || group.groupType

            return (
              <div key={group.groupId} className="space-y-1">
                {/* Group header — clickable to edit group */}
                <div
                  onClick={() => onSelectGroup(group.groupId)}
                  className={cn(
                    "p-3 rounded-lg border cursor-pointer transition-colors hover:bg-accent/50",
                    selected ? "bg-accent border-primary" : "bg-card border-border"
                  )}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleGroup(group.groupId) }}
                        className="p-0.5 hover:bg-muted rounded"
                      >
                        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      <span className="text-sm font-medium text-muted-foreground">
                        第 {group.flatIndices[0] + 1}-{group.flatIndices[group.flatIndices.length - 1] + 1} 题
                      </span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {groupLabel}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 ml-6">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs text-muted-foreground truncate">
                      {group.passagePreview || `(${group.subQuestions.length} 小题)`}
                    </span>
                  </div>
                </div>

                {/* Sub-questions (when expanded) */}
                {expanded && (
                  <div className="ml-4 space-y-1">
                    {group.subQuestions.map((sq, subIdx) => {
                      const isSelected = sq.question_id === selectedQuestionId
                      return (
                        <QuestionRow
                          key={sq.question_id}
                          q={sq}
                          index={subIdx + 1}
                          isSelected={isSelected}
                          onSelect={() => onSelect(sq.question_id)}
                          onInsertAfter={() => onInsertAfter(sq.question_id)}
                          onDelete={() => onDelete(sq.question_id)}
                          compact
                        />
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </ScrollArea>
    </div>
  )
}

/** Single question row */
function QuestionRow({
  q,
  index,
  isSelected,
  onSelect,
  onInsertAfter,
  onDelete,
  compact = false,
}: {
  q: ExamPaperQuestionItem
  index: number
  isSelected: boolean
  onSelect: () => void
  onInsertAfter: () => void
  onDelete: () => void
  compact?: boolean
}) {
  return (
    <div
      onClick={onSelect}
      className={cn(
        "p-3 rounded-lg border cursor-pointer transition-colors hover:bg-accent/50",
        compact ? "p-2" : "",
        isSelected ? "bg-accent border-primary" : "bg-card border-border"
      )}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={cn("font-medium text-muted-foreground", compact ? "text-xs" : "text-sm")}>
          {compact ? `${index}.` : `第 ${index} 题`}
        </span>
        <div className="flex items-center gap-1">
          <Badge variant="outline" className="text-xs">
            {q.type || '未知题型'}
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => { e.stopPropagation(); onInsertAfter() }}
          >
            <Plus className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={(e) => { e.stopPropagation(); onDelete() }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <div className={cn("text-sm line-clamp-2 text-foreground/90 overflow-hidden relative", compact ? "h-8" : "h-10")}>
        {q.content?.stem ? (
          <div className="absolute inset-0">
            <LatexText className="line-clamp-2">{q.content.stem}</LatexText>
          </div>
        ) : (
          <span className="text-muted-foreground italic">暂无题干内容</span>
        )}
      </div>
      {!compact && (
        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
          {q.score && <span>{q.score} 分</span>}
          {q.difficulty && <span>难度: {q.difficulty}</span>}
        </div>
      )}
    </div>
  )
}
